import { fetchData, PaginationParameters, PaginationResponse } from "@request";
import { getQueryParams } from "@request/helpers";

export type RequisiteType = "SBP" | "CARD" | "ACCOUNT";

export type RequisiteForm = PaginationParameters<{
  at_work?: boolean;
  active?: boolean;
  scheduled_active?: boolean;
  deleted?: boolean;
  min_amount?: number;
  max_amount?: number;
  start_date?: string;
  end_date?: string;
  date_field?: "created_at" | "updated_at";
  sort_by?: string;
  sort_desc?: boolean;
}>;

type Bank = {
  id: string;
  name: string;
  bank_id_code: string;
};

export type Requisite = {
  id: string;
  device_id: string;
  device_name: string;
  type: RequisiteType;
  active: boolean;
  at_work: boolean;
  deleted: boolean;
  full_name: string;
  phone_number: string;
  card_number: string;
  account_number: string;
  balance: number;
  bank: Bank;
  created_at: string;
  updated_at: string;
  currency: string;
  recipient_name: string;
  total_orders?: number;
  success_orders?: number;
  conversion?: number;
  start_time?: string;
  end_time?: string;
  scheduled_active?: boolean;
  scheduler_tooltip?: string;
  intrabank_only?: boolean;
};

export type RequisiteResponse = PaginationResponse<{
  requisites: Requisite[];
  total_count: number;
}>;

export const getRequisites = async (userId: string, data: RequisiteForm) => {
  try {
    const queryParams = getQueryParams(data);

    const response = await fetchData<RequisiteResponse>({
      url: `api/v1/requisites/by-user/${userId}?${queryParams}`,
    });

    return response;
  } catch (error) {
    console.error("Requisites failed:", error);
    throw error;
  }
};

export type OrderRequisite = {
  success: boolean;
  data: OrderRequisiteData;
};

export type OrderRequisiteData = {
  order_id: string;
  amount: number;
  back_url?: string;
  payment_method?: string;
  card_number?: string;
  full_name?: string;
  bank_name?: string;
  status: string;
  created_at: string;
  expired_at_unix: number;
};

export const getOrderRequisitesApi = async (orderId: string) => {
  try {
    const response = await fetchData<OrderRequisite>({
      url: `api/v1/public/orders/${orderId}/requisites`,
    });

    return response;
  } catch (error) {
    console.error("getOrderRequisites failed:", error);
    throw error;
  }
};

export const confirmOrderApi = async (userId: string, orderId: string) => {
  try {
    const response = await fetchData<string>({
      method: "POST",
      url: `api/v1/users/${userId}/order/${orderId}`,
    });

    return response;
  } catch (error) {
    console.error("Confirm order failed:", error);
    throw error;
  }
};

export type CreateRequisiteForm = {
  account_number: string;
  bank_id: string;
  card_number: string;
  currency: string;
  full_name: string;
  phone_number: string;
  type: RequisiteType;
  userId: string;
  device_id?: string;
  active?: boolean;
  recipient_name?: string;
  owner?: string;

  start_time?: string | null;
  end_time?: string | null;
  max_transaction?: number | null;
  min_transaction?: number | null;
  max_amount_per_period?: number | null;
  min_interval?: number | null;
  transaction_count?: number | null;

  intrabank_only?: boolean;
};

export const createRequisite = async (data: CreateRequisiteForm) => {
  try {
    const response = await fetchData<string>({
      method: "POST",
      url: `api/v1/requisites`,
      data,
    });

    return response;
  } catch (error) {
    console.error("Create requisite failed:", error);
    throw error;
  }
};

export const removeRequisite = async (requisiteId: string) => {
  try {
    const response = await fetchData<string>({
      method: "DELETE",
      url: `api/v1/requisites/by-id/${requisiteId}`,
    });

    return response;
  } catch (error) {
    console.error("Remove requisite failed:", error);
    throw error;
  }
};

export type DetailRequisite = Requisite & {
  start_time: string;
  end_time: string;
  max_amount_per_period: number;
  max_transaction: number;
  min_transaction: number;
  min_interval: number;
  transaction_count: number;
  created_at_unix: string;
  updated_at_unix: string;
};
export type DetailRequisiteResponse = {
  success: boolean;
  data: DetailRequisite;
};

export const getRequisite = async (requisiteId: string) => {
  try {
    const response = await fetchData<DetailRequisiteResponse>({
      url: `api/v1/requisites/by-id/${requisiteId}/detail`,
    });

    return response;
  } catch (error) {
    console.error("Get requisite failed:", error);
    throw error;
  }
};

export type UpdateRequisiteForm = {
  requisiteId: string;
  account_number?: string;
  active?: boolean;
  bank_id?: string;
  card_number?: string;
  currency?: string;
  device_id?: string;
  end_time?: string;
  full_name?: string;
  max_amount_per_period?: number;
  max_transaction?: number;
  min_interval?: number;
  min_transaction?: number;
  phone_number?: string;
  recipient_name?: string;
  start_time?: string;
  transaction_count?: number;
  type?: RequisiteType;
  intrabank_only?: boolean;
};

export const updateRequisite = async (data: UpdateRequisiteForm) => {
  try {
    const response = await fetchData<string>({
      method: "PUT",
      url: `api/v1/requisites/by-id/${data.requisiteId}`,
      data,
    });

    return response;
  } catch (error) {
    console.error("Update requisite failed:", error);
    throw error;
  }
};

export type ActivateRequisiteForm = {
  requisite: string;
  type_confirm: "sbp" | "account_transfer" | "card_transfer";
};

export type ActivateRequisiteResponse = {
  amount: number;
  time_remaining: number;
};

export const activateRequisite = async (
  userId: string,
  data: ActivateRequisiteForm
) => {
  try {
    const response = await fetchData<ActivateRequisiteResponse>({
      method: "POST",
      url: `api/v1/users/activate-requisite/${userId}`,
      data,
    });

    return response;
  } catch (error) {
    console.log("Activate requisite failed:", error);
    throw error;
  }
};

export type EnableRequisiteForm = {
  at_work: boolean;
};

export const enableRequisite = async (
  id: string,
  data: EnableRequisiteForm
) => {
  try {
    const response = await fetchData<string>({
      method: "PUT",
      url: `api/v1/requisites/by-id/${id}/at-work`,
      data,
    });

    return response;
  } catch (error) {
    console.error("Enable requisite failed:", error);
    throw error;
  }
};
